/**
 * applicator-tally.v1 cizici — UYGULAYICI tarafindan uygulanan ve puanlanan soru.
 *
 * "EKRANI ÇOCUĞA GÖSTERMEYİN": ekran uygulayiciya doner. Uygulayici her maddeyi
 * (ör. sesleri ayirarak) bir kez okur, cocuk sozlu yanit verir, uygulayici her
 * madde icin Doğru/Yanlış isaretler. Cocuk ekranla etkilesmez.
 *
 * Icerik: { applicator?, prompt?, items:[{id,sounds,word}] }
 *   - sounds : uygulayicinin okuyacagi ayrik sesler (ör. "A – R – I")
 *   - word   : beklenen sozcuk (uygulayici dogrulamasi icin gorunur)
 * Cevap: onAnswerChange({ results:{id:bool}, correct:N }); tum maddeler
 *   isaretlenene kadar null (ileri kapali).
 */
import { useEffect, useRef, useState } from "react";
import type { MechanicProps } from "./types";

interface TItem {
  id: string;
  /** ayrik sesler (buyuk, harf araligi acik) — ör. "A – R – I". */
  sounds?: string;
  /** normal cumle/yonerge metni — ör. "Bıçak kesmek için, Terlik. …". */
  text?: string;
  /** beklenen sozcuk (sounds ile). */
  word?: string;
  /** ornek/beklenen tamamlama (text ile). */
  expected?: string;
  /** gorselli tally (ifade edici dil): madde ustunde sahne gorseli. */
  imageUrl?: string;
  /** ara sorular (madde madde gosterilir) — ör. s38 dikkat dagitma sorulari. */
  bullets?: string[];
  /** madde-bazli uyaran sesi (ör. B6 kelime dizisi) — sayfali kartta "dinlet" dugmesi. */
  audioUrl?: string;
}
interface TContent {
  applicator?: { warning?: string; text?: string; example?: string };
  prompt?: { text?: string };
  items?: TItem[];
  /** true: maddeler TEK TEK gosterilir (ileri/geri). Gorselli maddede otomatik. */
  paginate?: boolean;
  /** uygulayicinin cocuga BIR KEZ dinletecegi hikaye/uyaran sesi. */
  audioUrl?: string;
  /** ses dugmesi etiketi (ör. "Hikâyeyi dinlet" / "Diziyi dinlet"). */
  audioLabel?: string;
  /** true: "ilk hatada dur" kurali — bir maddeye Yanlis basilinca sonraki maddeler de Yanlis isaretlenir. */
  stopOnFirstError?: boolean;
}

/**
 * Madde metnini biçimler: "BALİNA — 'Lİ' atılırsa" -> kelime ECUP morunda,
 * kalanı normal, sonuna mor "→". Ayraç yoksa metni oldugu gibi dondurur.
 */
function formatTallyText(text?: string) {
  if (!text) return null;
  const dash = text.indexOf("—");
  if (dash === -1) return text;
  const word = text.slice(0, dash).trim();
  const rest = text.slice(dash + 1).trim();
  return (
    <>
      <span style={{ color: "#820AD1", fontWeight: 700 }}>{word}</span>{" "}
      <span style={{ color: "#820AD1", fontWeight: 700 }}>→</span> {rest}
    </>
  );
}

/**
 * Bir maddeyi isaretler. Ilk-hatada-dur kuralinda: uygulayicinin isaretledigi ILK Yanlis maddeden sonraki
 * isaretsiz maddeler kendiliginden Yanlis olur; o Yanlis duzeltilirse bu kendiliginden isaretler kalkar
 * (uygulayicinin kendi isaretleri korunur).
 */
export function applyTallyMark(
  ids: string[],
  results: Record<string, boolean>,
  auto: string[],
  id: string,
  value: boolean,
  stopOnFirstError: boolean,
): { results: Record<string, boolean>; auto: string[] } {
  const r: Record<string, boolean> = { ...results, [id]: value };
  if (!stopOnFirstError) return { results: r, auto: auto.filter((x) => x !== id) };
  for (const x of auto) if (x !== id) delete r[x];
  const first = ids.findIndex((x) => r[x] === false);
  const nextAuto: string[] = [];
  if (first >= 0) {
    for (const x of ids.slice(first + 1)) {
      if (!(x in r)) {
        r[x] = false;
        nextAuto.push(x);
      }
    }
  }
  return { results: r, auto: nextAuto };
}

export function ApplicatorTallyItem({ item, onAnswerChange }: MechanicProps) {
  const c = item.content as unknown as TContent;
  const items = c.items ?? [];
  const saved = item.answer?.results as Record<string, boolean> | undefined;
  const [results, setResults] = useState<Record<string, boolean>>(saved ?? {});
  const [page, setPage] = useState(0);

  // hikaye/dizi sesi: ONCE ses ekrani; ses bitince sorular acilir (gated).
  const audio = useRef<HTMLAudioElement | null>(null);
  const [playing, setPlaying] = useState(false);
  const gated = !!c.audioUrl;
  const [revealed, setRevealed] = useState(!gated);

  // ogretmene donuk ORNEK kutusu (mantigi somutlastirir) — varsa uyarinin altinda.
  const exampleEl = c.applicator?.example ? (
    <div
      style={{
        margin: "0 auto 12px",
        maxWidth: 760,
        padding: "10px 16px",
        background: "#f6ecfd",
        border: "1.5px solid #820AD1",
        borderRadius: 12,
        color: "#4b2673",
        fontSize: 14,
        lineHeight: 1.45,
      }}
    >
      <span style={{ fontWeight: 700, color: "#820AD1" }}>Örnek: </span>
      {c.applicator.example}
    </div>
  ) : null;

  // Ilk-hatada-dur kuralinda kendiliginden Yanlis yapilan maddeler (uygulayicinin isaretlemedikleri).
  const [auto, setAuto] = useState<string[]>([]);
  function isaretle(id: string, value: boolean) {
    const out = applyTallyMark(items.map((x) => x.id), results, auto, id, value, !!c.stopOnFirstError);
    setResults(out.results);
    setAuto(out.auto);
  }

  const cb = useRef(onAnswerChange);
  cb.current = onAnswerChange;
  useEffect(() => {
    const allMarked = items.length > 0 && items.every((it) => it.id in results);
    if (allMarked) {
      const correct = items.filter((it) => results[it.id]).length;
      cb.current({ results, correct });
    } else {
      const correct = items.filter((it) => results[it.id]).length;
      cb.current(null, Object.keys(results).length > 0 ? { results, correct } : null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [results]);

  // ---- SES GECIDI: once yalnizca ses ekrani; ses bitince sorular acilir ----
  if (gated && !revealed) {
    return (
      <div className="mech tally-mech">
        {c.applicator?.text && <h2 className="prompt tally-instruction">{c.applicator.text}</h2>}
        {c.applicator?.warning && <p className="tally-warn-note">{c.applicator.warning}</p>}
        <div className="tally-audio">
          {playing ? (
            <div className="waveform" aria-label="ses çalıyor">
              <span /><span /><span /><span /><span /><span /><span />
            </div>
          ) : (
            <button
              type="button"
              className="big-action-btn"
              onClick={() => { const a = audio.current; if (a) { a.currentTime = 0; setPlaying(true); void a.play(); } }}
            >
              {"▶ " + (c.audioLabel ?? "Hikâyeyi dinlet")}
            </button>
          )}
          <audio ref={audio} src={c.audioUrl} preload="auto" onEnded={() => { setPlaying(false); setRevealed(true); }} />
        </div>
      </div>
    );
  }

  // ---- gorselli tally (Ifade Edici Dil): BIR BIR madde (resim + cumle), ileri/geri ----
  const hasImages = items.some((it) => !!it.imageUrl);
  if ((hasImages || c.paginate) && items.length > 0) {
    const pageCount = items.length;
    const p = Math.min(page, pageCount - 1);
    const it = items[p];
    if (!it) return null;
    const mark = results[it.id];
    const ref = it.word ?? it.expected;
    return (
      <div className="mech tally-mech">
        {c.applicator?.text && <h2 className="prompt tally-instruction">{c.applicator.text}</h2>}
        {c.applicator?.warning && <p className="tally-warn-note">{c.applicator.warning}</p>}
        {exampleEl}
        <div className="tally-card">
          {it.imageUrl && (
            <img src={it.imageUrl} alt="" style={{ width: "100%", maxHeight: 400, objectFit: "contain", borderRadius: 10 }} />
          )}
          {it.sounds && <div className="tally-page-sounds">{it.sounds}</div>}
          {it.text && <div className="tally-q">{it.text}</div>}
          {it.audioUrl && (
            <div className="tally-audio">
              <button
                type="button"
                className="big-action-btn"
                onClick={() => { const a = audio.current; if (a) { a.currentTime = 0; setPlaying(true); void a.play(); } }}
              >
                {playing ? "▶ Çalıyor…" : "▶ Diziyi dinlet"}
              </button>
              <audio ref={audio} src={it.audioUrl} preload="auto" onEnded={() => setPlaying(false)} />
            </div>
          )}
          <div style={{ display: "flex", justifyContent: "center", gap: 16 }}>
            <button type="button" className={`tally-btn ok${mark === true ? " on" : ""}`} style={{ padding: "14px 34px", fontSize: "1.15rem" }} onClick={() => isaretle(it.id, true)}>Doğru</button>
            <button type="button" className={`tally-btn no${mark === false ? " on" : ""}`} style={{ padding: "14px 34px", fontSize: "1.15rem" }} onClick={() => isaretle(it.id, false)}>Yanlış</button>
          </div>
          {ref && <div style={{ textAlign: "center", color: "#6b7280", fontSize: 14 }}>Beklenen: {ref}</div>}
        </div>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 24, marginTop: 18 }}>
          <button type="button" className="tally-btn" disabled={p === 0} onClick={() => { setPlaying(false); setPage(p - 1); }} style={{ opacity: p === 0 ? 0.4 : 1 }}>‹ geri</button>
          <span style={{ fontWeight: 700, color: "#374151" }}>{p + 1} / {pageCount}</span>
          <button type="button" className="tally-btn" disabled={p >= pageCount - 1} onClick={() => { setPlaying(false); setPage(p + 1); }} style={{ opacity: p >= pageCount - 1 ? 0.4 : 1 }}>ileri ›</button>
        </div>
        <p style={{ textAlign: "center", color: "#9aa0a6", fontSize: 12, marginTop: 10 }}>
          Tüm maddeler işaretlenince ileri gidebilirsin.
        </p>
      </div>
    );
  }

  return (
    <div className="mech tally-mech">
      {c.applicator?.text && <h2 className="prompt tally-instruction">{c.applicator.text}</h2>}
      {c.applicator?.warning && <p className="tally-warn-note">{c.applicator.warning}</p>}
      {exampleEl}

      <div className={`tally-list${items.length > 4 ? " two-col" : ""}`}>
        {items.map((it) => {
          const mark = results[it.id];
          const ref = it.word ?? it.expected;
          const hasBullets = Array.isArray(it.bullets) && it.bullets.length > 0;
          const buttons = (
            <span className="tally-btns">
              <button
                type="button"
                className={`tally-btn ok${mark === true ? " on" : ""}`}
                onClick={() => isaretle(it.id, true)}
              >
                Doğru
              </button>
              <button
                type="button"
                className={`tally-btn no${mark === false ? " on" : ""}`}
                onClick={() => isaretle(it.id, false)}
              >
                Yanlış
              </button>
            </span>
          );
          if (hasBullets) {
            return (
              <div className="tally-row col" key={it.id}>
                <div className="tally-main">
                  <span className="tally-text">{it.text}</span>
                </div>
                <ul className="tally-bullets">
                  {it.bullets!.map((b, j) => (
                    <li key={j}>{b}</li>
                  ))}
                </ul>
                <div className="tally-foot">
                  {ref && <span className="tally-word">Beklenen: {ref}</span>}
                  {buttons}
                </div>
              </div>
            );
          }
          return (
            <div className="tally-row col" key={it.id}>
              <div className="tally-main" style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
                {it.sounds ? (
                  <span className="tally-sounds">{it.sounds}</span>
                ) : (
                  <span className="tally-text">{formatTallyText(it.text)}</span>
                )}
                {buttons}
              </div>
              {ref && (
                <div className="tally-expected" style={{ textAlign: "center", marginTop: 6, color: "#6b7280", fontSize: 13 }}>
                  Beklenen: {ref}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
